import { useState } from 'react'
import type { LostReason } from '@1130-studio/shared'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'

interface LostReasonModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: LostReason, comment?: string) => void
  isLoading?: boolean
}

const lostReasons: Array<{ value: LostReason; label: string }> = [
  { value: 'price', label: 'Нет бюджета' },
  { value: 'competitor', label: 'Выбрали конкурента' },
  { value: 'no_response', label: 'Не отвечает' },
  { value: 'timing', label: 'Не подходит срок' },
  { value: 'not_qualified', label: 'Не целевой' },
  { value: 'spam', label: 'Спам' },
  { value: 'postponed', label: 'Отложено' },
  { value: 'other', label: 'Другое' },
]

export function LostReasonModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: LostReasonModalProps) {
  const [selectedReason, setSelectedReason] = useState<LostReason | null>(null)
  const [comment, setComment] = useState('')

  if (!isOpen) return null

  const handleConfirm = () => {
    if (!selectedReason) return
    onConfirm(selectedReason, comment || undefined)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4">
      <div className="glass-card w-full max-w-md rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <h2 className="text-lg font-bold text-white">
            Причина потери
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Укажите причину отказа от заявки
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Reason Options */}
          <div className="space-y-2">
            {lostReasons.map((reason) => (
              <label
                key={reason.value}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all',
                  'border border-transparent',
                  selectedReason === reason.value
                    ? 'bg-accent-orange/10 border-accent-orange/30'
                    : 'bg-white/5 hover:bg-white/10'
                )}
              >
                <input
                  type="radio"
                  name="lostReason"
                  value={reason.value}
                  checked={selectedReason === reason.value}
                  onChange={() => setSelectedReason(reason.value)}
                  className="sr-only"
                />
                <div
                  className={cn(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
                    selectedReason === reason.value
                      ? 'border-accent-orange bg-accent-orange'
                      : 'border-text-tertiary'
                  )}
                >
                  {selectedReason === reason.value && (
                    <div className="w-1.5 h-1.5 rounded-full bg-black" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm transition-colors',
                    selectedReason === reason.value ? 'text-white' : 'text-text-secondary'
                  )}
                >
                  {reason.label}
                </span>
              </label>
            ))}
          </div>

          {/* Comment (shown when any reason is selected) */}
          {selectedReason && (
            <div>
              <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                Комментарий
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full h-24 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-orange/50 focus:ring-1 focus:ring-accent-orange/20 resize-none"
                placeholder="Дополнительные детали (опционально)..."
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-white/10">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Отмена
          </Button>
          <Button
            variant="danger"
            className="flex-1"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedReason}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    </div>
  )
}
